import React from "react";
import {
  ArrowCounterClockwiseIcon,
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  EnvelopeSimpleIcon,
  FileMagnifyingGlassIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import type { ExternalCourse } from "@axio/graphql/generated/graphql";
import AdminTable from "../AdminTable/AdminTable";
import AdminTablePagination from "../AdminTable/AdminTablePagination";
import FilterDropdown from "../../common/TableControls/FilterDropdown/FilterDropdown";
import CustomSearchInput from "../../common/general/CustomSearchInput/CustomSearchInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import Tooltip from "../../common/tooltip/Tooltip";
import CourseTermSelect from "./CourseTermSelect";
import type { CourseTermOption } from "./courseTermOptions";
import { ExternalCourseListSourceType } from "@axio/graphql/generated/graphql";

type FilterItem = {
  label: string;
  value: string;
};

export type CourseModernizerRowStatusTone = "inProgress" | "modernized" | "notStarted" | "default";

const ACTION_TOOLTIP_WRAP_AFTER_LENGTH = 45;

export type CourseModernizerRowActionId =
  | "start"
  | "editShell"
  | "openWizard"
  | "evaluate"
  | "rerun"
  | "reset"
  | "viewResults";

export type CourseModernizerRowAction = {
  id: CourseModernizerRowActionId;
  label: string;
  requiresReadyRubric?: boolean;
};

export type CourseModernizerRowArchitect = {
  id: string;
  name: string;
  email: string | null;
  profilePicture: string | null;
};

export type CourseModernizerCourseRow = {
  course: ExternalCourse;
  termLabel: string | null;
  architect: CourseModernizerRowArchitect | null;
  statusLabel: string;
  statusTone: CourseModernizerRowStatusTone;
  primaryAction: CourseModernizerRowAction | null;
  secondaryActions: CourseModernizerRowAction[];
  activeModernizedCourseId: string | null;
  modernizerIntakeId: string | null;
};

type CourseModernizerCoursesTableProps = {
  isAdmin: boolean;
  loading: boolean;
  visibleCourses: CourseModernizerCourseRow[];
  totalItems: number;
  page: number;
  pageSize: number;
  facultyFilter: string;
  facultyFilterDropdownOpen: boolean;
  termFilter: string;
  termFilterDropdownOpen: boolean;
  sourceFilter: string;
  sourceFilterDropdownOpen: boolean;
  searchQuery: string;
  facultyOptions: FilterItem[];
  termItems: FilterItem[];
  sourceItems: FilterItem[];
  termOptions: CourseTermOption[];
  evaluationDisabledReason: string | null;
  courseTermDropdownCourseId: string | null;
  backgroundEvaluationCourseId?: string | null;
  backgroundEvaluationPercent?: number;
  isCancellingBackgroundEvaluation?: boolean;
  onCancelBackgroundEvaluation?: () => void;
  onFacultyFilterChange: (value: string) => void;
  setFacultyFilterDropdownOpen: (open: boolean) => void;
  onTermFilterChange: (value: string) => void;
  setTermFilterDropdownOpen: (open: boolean) => void;
  onSourceFilterChange: (value: string) => void;
  setSourceFilterDropdownOpen: (open: boolean) => void;
  onCourseTermDropdownChange: (courseId: string | null) => void;
  onCourseTermChange: (courseId: string, term: string) => void;
  onCourseTermClear: (courseId: string) => void;
  onSearchQueryChange: (value: string) => void;
  onRowAction: (row: CourseModernizerCourseRow, actionId: CourseModernizerRowActionId) => void;
  onViewResults: (courseId: string) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (pageSize: number) => void;
};

function CourseModernizerCoursesTable({
  isAdmin,
  loading,
  visibleCourses,
  totalItems,
  page,
  pageSize,
  facultyFilter,
  facultyFilterDropdownOpen,
  termFilter,
  termFilterDropdownOpen,
  sourceFilter,
  sourceFilterDropdownOpen,
  searchQuery,
  facultyOptions,
  termItems,
  sourceItems,
  termOptions,
  evaluationDisabledReason,
  courseTermDropdownCourseId,
  backgroundEvaluationCourseId = null,
  backgroundEvaluationPercent = 0,
  isCancellingBackgroundEvaluation = false,
  onCancelBackgroundEvaluation,
  onFacultyFilterChange,
  setFacultyFilterDropdownOpen,
  onTermFilterChange,
  setTermFilterDropdownOpen,
  onSourceFilterChange,
  setSourceFilterDropdownOpen,
  onCourseTermDropdownChange,
  onCourseTermChange,
  onCourseTermClear,
  onSearchQueryChange,
  onRowAction,
  onViewResults,
  onPageChange,
  onItemsPerPageChange,
}: CourseModernizerCoursesTableProps) {
  const evaluationActionsDisabled = Boolean(evaluationDisabledReason);

  const getStatusTagStyles = (statusTone: CourseModernizerRowStatusTone) => {
    switch (statusTone) {
      case "inProgress":
        return { tag: styles.inProgressStatusTag, text: styles.inProgressStatusTagText };
      case "modernized":
        return { tag: styles.modernizedStatusTag, text: styles.modernizedStatusTagText };
      case "notStarted":
        return { tag: styles.notStartedStatusTag, text: styles.notStartedStatusTagText };
      case "default":
      default:
        return { tag: styles.defaultStatusTag, text: styles.defaultStatusTagText };
    }
  };

  const renderArchitect = (architect: CourseModernizerRowArchitect | null) => {
    if (!architect) {
      return <CustomText text="—" size="xs" useTranslationText={false} style={styles.noScoreText} />;
    }

    const initials = architect.name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

    return (
      <View style={styles.architectRow}>
        {architect.profilePicture ? (
          <Image source={{ uri: architect.profilePicture }} style={styles.architectAvatar} accessible={false} />
        ) : (
          <View style={styles.architectInitialsAvatar}>
            <CustomText
              text={initials || "?"}
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.architectInitialsText}
            />
          </View>
        )}
        <View style={styles.architectTextColumn}>
          <CustomText text={architect.name} size="s" useTranslationText={false} style={styles.architectNameText} />
          {architect.email ? (
            <CustomText text={architect.email} size="xs" useTranslationText={false} style={styles.secondaryText} />
          ) : null}
        </View>
      </View>
    );
  };

  const renderActionButton = (
    row: CourseModernizerCourseRow,
    action: CourseModernizerRowAction,
    isPrimary: boolean
  ) => {
    const actionDisabled = action.requiresReadyRubric === true && evaluationActionsDisabled;
    const isRerun = action.id === "rerun";
    const isReset = action.id === "reset";
    const isSetupStartAction = action.id === "start" || action.id === "editShell";
    const isOpenWizard = action.id === "openWizard";
    const usesPrimaryActionStyle = isPrimary && !isOpenWizard;
    const isViewResults = action.id === "viewResults";
    const setupIconColor = usesPrimaryActionStyle ? "#FFFFFF" : "#475467";
    const leftIcon = isRerun ? (
      <ArrowsClockwiseIcon size={14} color="#475467" weight="bold" />
    ) : isReset ? (
      <ArrowCounterClockwiseIcon size={14} color="#475467" weight="bold" />
    ) : isSetupStartAction ? (
      <SparkleIcon size={16} color={setupIconColor} weight="bold" />
    ) : isOpenWizard ? (
      <EnvelopeSimpleIcon size={16} color="#475467" weight="bold" />
    ) : isPrimary ? (
      <FileMagnifyingGlassIcon size={12} color="#FFFFFF" weight="bold" />
    ) : null;
    const rightIcon = isViewResults ? <ArrowRightIcon size={14} color="#475467" weight="bold" /> : null;

    const button = (
      <CustomButton
        text={action.label}
        styleType={usesPrimaryActionStyle ? "primary" : "lightStroke"}
        size="s"
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        style={usesPrimaryActionStyle ? styles.evaluateButton : styles.secondaryActionButton}
        textStyle={usesPrimaryActionStyle ? styles.evaluateButtonText : styles.secondaryActionButtonText}
        useTranslationText={false}
        disabled={actionDisabled}
        onPress={
          actionDisabled
            ? undefined
            : () => {
                if (action.id === "viewResults" && row.activeModernizedCourseId) {
                  onViewResults(row.activeModernizedCourseId);
                  return;
                }
                onRowAction(row, action.id);
              }
        }
      />
    );

    if (!action.requiresReadyRubric) {
      return button;
    }

    return (
      <Tooltip
        content={evaluationDisabledReason ?? undefined}
        placement="top"
        showArrow={false}
        maxWidth={360}
        wrapTextAfterLength={ACTION_TOOLTIP_WRAP_AFTER_LENGTH}>
        {button}
      </Tooltip>
    );
  };

  const hasActiveFilters =
    facultyFilter !== "all" || termFilter !== "all" || sourceFilter !== "all" || searchQuery.trim().length > 0;

  const handleFacultyDropdownOpenChange = (open: boolean) => {
    setFacultyFilterDropdownOpen(open);
    if (open) {
      setTermFilterDropdownOpen(false);
      setSourceFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleTermDropdownOpenChange = (open: boolean) => {
    setTermFilterDropdownOpen(open);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setSourceFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleSourceDropdownOpenChange = (open: boolean) => {
    setSourceFilterDropdownOpen(open);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setTermFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleCourseTermDropdownOpenChange = (courseId: string, open: boolean) => {
    onCourseTermDropdownChange(open ? courseId : null);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setTermFilterDropdownOpen(false);
      setSourceFilterDropdownOpen(false);
    }
  };

  const handleClearAll = () => {
    onFacultyFilterChange("all");
    onTermFilterChange("all");
    onSourceFilterChange("all");
    onSearchQueryChange("");
    setFacultyFilterDropdownOpen(false);
    setTermFilterDropdownOpen(false);
    setSourceFilterDropdownOpen(false);
    onCourseTermDropdownChange(null);
    onPageChange(1);
  };

  return (
    <AdminTable style={styles.adminTable}>
      <View style={styles.tableControlsRow}>
        <View style={styles.filtersRow}>
          {isAdmin && (
            <FilterDropdown
              filterValue={facultyFilter}
              setFilterValue={onFacultyFilterChange}
              filterDropdownOpen={facultyFilterDropdownOpen}
              setFilterDropdownOpen={handleFacultyDropdownOpenChange}
              filterItems={facultyOptions}
              placeholder="All Faculty"
            />
          )}
          <FilterDropdown
            filterValue={termFilter}
            setFilterValue={onTermFilterChange}
            filterDropdownOpen={termFilterDropdownOpen}
            setFilterDropdownOpen={handleTermDropdownOpenChange}
            filterItems={termItems}
            placeholder="All Terms"
          />
          <FilterDropdown
            filterValue={sourceFilter}
            setFilterValue={onSourceFilterChange}
            filterDropdownOpen={sourceFilterDropdownOpen}
            setFilterDropdownOpen={handleSourceDropdownOpenChange}
            filterItems={sourceItems}
            placeholder="All Sources"
          />
          {hasActiveFilters ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear all filters"
              onPress={handleClearAll}
              style={styles.clearAllButton}>
              <CustomText text="Clear all" size="s" useTranslationText={false} style={styles.clearAllText} />
            </Pressable>
          ) : null}
        </View>
        <View style={styles.searchInputWrapper}>
          <CustomSearchInput
            placeholder={isAdmin ? "Search courses, codes, or faculty" : "Search courses or codes"}
            searchQuery={searchQuery}
            setSearchQuery={onSearchQueryChange}
          />
        </View>
      </View>
      <View style={styles.tableHeaderRow}>
        <CustomText
          text="Course Name"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.courseColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Term"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.termColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Faculty"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.facultyColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Status"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.statusColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Architect"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.architectColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Actions"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.actionsColumn]}
          useTranslationText={false}
        />
      </View>

      {loading ? (
        <View style={styles.emptyState}>
          <CustomText text="Loading external courses..." size="s" useTranslationText={false} />
        </View>
      ) : visibleCourses.length === 0 ? (
        <View style={styles.emptyState}>
          <CustomText text="No external courses found" size="s" useTranslationText={false} />
        </View>
      ) : (
        visibleCourses.map((row) => {
          const courseTermDropdownOpen = courseTermDropdownCourseId === row.course.id;
          const statusTagStyles = getStatusTagStyles(row.statusTone);
          const sourceLabel = row.course.sourceLabel;

          return (
            <View key={row.course.id} style={[styles.tableRow, courseTermDropdownOpen && styles.tableRowWithOpenMenu]}>
              <View style={[styles.tableCell, styles.courseColumn]}>
                <CustomText
                  text={row.course.name || "Untitled course"}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                />
                <CustomText
                  text={row.course.courseCode || row.course.providerCourseId || "-"}
                  size="xs"
                  style={styles.secondaryText}
                  useTranslationText={false}
                />
                {sourceLabel && sourceLabel !== ExternalCourseListSourceType.Lms ? (
                  <View style={styles.sourceBadge}>
                    <CustomText
                      text={sourceLabel}
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.sourceBadgeText}
                    />
                  </View>
                ) : null}
              </View>
              <View
                style={[styles.tableCell, styles.termColumn, courseTermDropdownOpen && styles.termCellWithOpenMenu]}>
                <CourseTermSelect
                  value={row.termLabel}
                  options={termOptions}
                  open={courseTermDropdownOpen}
                  onOpenChange={(open) => handleCourseTermDropdownOpenChange(row.course.id, open)}
                  onChange={(term) => onCourseTermChange(row.course.id, term)}
                  onClear={() => onCourseTermClear(row.course.id)}
                  variant="row"
                  accessibilityLabel={`Term for ${row.course.name || "course"}`}
                />
              </View>
              <View style={[styles.tableCell, styles.facultyColumn]}>
                <CustomText
                  text={
                    row.course.teachingFaculty
                      ?.map((faculty) => faculty?.name)
                      .filter(Boolean)
                      .join(", ") || "-"
                  }
                  size="s"
                  useTranslationText={false}
                />
              </View>
              <View style={[styles.tableCell, styles.statusColumn]}>
                {backgroundEvaluationCourseId === row.course.id ? (
                  <View style={styles.backgroundEvaluationRow}>
                    <View style={[styles.statusTag, styles.evaluatingStatusTag]}>
                      <CustomText
                        text={
                          isCancellingBackgroundEvaluation
                            ? "Cancelling..."
                            : `Evaluating ${backgroundEvaluationPercent}%`
                        }
                        size="s"
                        weight="bold"
                        useTranslationText={false}
                        style={styles.evaluatingStatusTagText}
                      />
                    </View>
                    {!isCancellingBackgroundEvaluation && (
                      <CustomButton
                        text="Cancel"
                        styleType="lightStroke"
                        size="s"
                        useTranslationText={false}
                        onPress={onCancelBackgroundEvaluation}
                        style={styles.backgroundCancelButton}
                        textStyle={styles.backgroundCancelButtonText}
                      />
                    )}
                  </View>
                ) : (
                  <View style={[styles.statusTag, statusTagStyles.tag]}>
                    <CustomText
                      text={row.statusLabel}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={[styles.statusTagText, statusTagStyles.text]}
                    />
                  </View>
                )}
              </View>
              <View style={[styles.tableCell, styles.architectColumn]}>{renderArchitect(row.architect)}</View>
              <View style={[styles.tableCell, styles.actionsColumn]}>
                <View style={styles.rowActions}>
                  {row.primaryAction ? renderActionButton(row, row.primaryAction, true) : null}
                  {row.secondaryActions.map((action) => (
                    <React.Fragment key={action.id}>{renderActionButton(row, action, false)}</React.Fragment>
                  ))}
                </View>
              </View>
            </View>
          );
        })
      )}
      {!loading && totalItems > 0 && (
        <AdminTablePagination
          currentPage={page}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
          navigationStyle="icons"
          containerStyle={styles.pagination}
        />
      )}
    </AdminTable>
  );
}

const styles = StyleSheet.create({
  adminTable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#EAECF0",
    overflow: "visible",
  },
  tableControlsRow: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    position: "relative",
    zIndex: 100,
    elevation: 100,
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  filtersRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    flex: 1,
  },
  clearAllButton: {
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  clearAllText: {
    color: "#3E68FE",
  },
  searchInputWrapper: {
    width: 320,
    maxWidth: "40%",
    minWidth: 260,
  },
  tableHeaderRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
    zIndex: 1,
    overflow: "visible",
  },
  tableRowWithOpenMenu: {
    zIndex: 1000,
    elevation: 1000,
  },
  tableHeaderCell: {
    color: "#475467",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontSize: 13,
  },
  tableCell: {
    justifyContent: "center",
  },
  termCellWithOpenMenu: {
    zIndex: 1000,
    elevation: 1000,
    overflow: "visible",
  },
  courseColumn: {
    flex: 2.2,
  },
  termColumn: {
    flex: 1.1,
  },
  facultyColumn: {
    flex: 1.4,
  },
  architectColumn: {
    flex: 1.5,
  },
  statusColumn: {
    flex: 1.1,
  },
  actionsColumn: {
    flex: 2.2,
  },
  secondaryText: {
    color: "#667085",
  },
  sourceBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#EEF4FF",
    borderColor: "#C7D7FE",
    borderRadius: 6,
    borderWidth: 1,
    marginTop: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  sourceBadgeText: {
    color: "#344054",
    fontSize: 12,
    lineHeight: 16,
  },
  statusTag: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "transparent",
    paddingHorizontal: 10,
    paddingVertical: 2,
    alignSelf: "flex-start",
  },
  statusTagText: {
    fontSize: 12,
    lineHeight: 18,
  },
  inProgressStatusTag: {
    backgroundColor: "#FEF0C7",
  },
  inProgressStatusTagText: {
    color: "#7A2E0E",
  },
  modernizedStatusTag: {
    backgroundColor: "#E3FBCC",
  },
  modernizedStatusTagText: {
    color: "#2B5314",
  },
  notStartedStatusTag: {
    backgroundColor: "#F2F4F7",
  },
  notStartedStatusTagText: {
    color: "#101828",
  },
  defaultStatusTag: {
    backgroundColor: "#F2F4F7",
  },
  defaultStatusTagText: {
    color: "#101828",
  },
  noScoreText: {
    color: "#667085",
    fontSize: 12,
    lineHeight: 18,
  },
  architectRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  architectAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  architectInitialsAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#EEF4FF",
    alignItems: "center",
    justifyContent: "center",
  },
  architectInitialsText: {
    color: "#3E68FE",
  },
  architectTextColumn: {
    minWidth: 0,
    flex: 1,
  },
  architectNameText: {
    color: "#101828",
  },
  rowActions: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  evaluateButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 9,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  evaluateButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },
  secondaryActionButton: {
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  secondaryActionButtonText: {
    color: "#475467",
    fontSize: 14,
    fontWeight: "600",
  },
  emptyState: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  pagination: {
    marginTop: 0,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
  },
  backgroundEvaluationRow: {
    gap: 6,
    alignItems: "flex-start",
  },
  evaluatingStatusTag: {
    backgroundColor: "#EFF8FF",
    borderColor: "#B2DDFF",
  },
  evaluatingStatusTagText: {
    color: "#175CD3",
    fontSize: 12,
    lineHeight: 18,
  },
  backgroundCancelButton: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  backgroundCancelButtonText: {
    color: "#B42318",
    fontSize: 12,
    fontWeight: "600",
  },
});

export default CourseModernizerCoursesTable;
